import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, InputAdornment, Divider, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import EuroIcon from '@mui/icons-material/Euro';
import GroupsIcon from '@mui/icons-material/Groups';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

export const PianiCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        nome: '',
        gruppoId: '',
        DescrizioneCompleta: '',
        maxNumeroSquadre: '',
        maxInfografiche: '',
        maxPostGg: '',
        maxPostWeek: '',
        maxPostMonth: '',
        imgQuality: '',
        prezzoMensile: '',
        prezzoAnnuale: '',
        prezziPersonalizzati: [],  // Array di {mesi: number, prezzo: number}
        durataDisponibile: ['mensile', 'annuale'],
        note: '',
        flVisibile: true,
        flAttivo: true,
        watermark: false,
        ordinamento: ''
    });

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [gruppi, setGruppi] = React.useState([]);
    const [loadingGruppi, setLoadingGruppi] = React.useState(true);

    // State per dialog conferma cancellazione
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [priceToDeleteIndex, setPriceToDeleteIndex] = React.useState(null);

    // Funzioni per gestire prezzi personalizzati
    const handleAddPrezzoPersonalizzato = () => {
        setFormData(prev => ({
            ...prev,
            prezziPersonalizzati: [...prev.prezziPersonalizzati, { mesi: '', prezzo: '', flAttivo: true }]
        }));
    };

    const handleRequestDeletePrezzo = (index) => {
        setPriceToDeleteIndex(index);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDeletePrezzo = () => {
        if (priceToDeleteIndex !== null) {
            setFormData(prev => ({
                ...prev,
                prezziPersonalizzati: prev.prezziPersonalizzati.filter((_, i) => i !== priceToDeleteIndex)
            }));
            setDeleteConfirmOpen(false);
            setPriceToDeleteIndex(null);
        }
    };

    const handlePrezzoPersonalizzatoChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            prezziPersonalizzati: prev.prezziPersonalizzati.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const loadGruppi = async () => {
        try {
            setLoadingGruppi(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: { flVisibile: 1, flAttivo: 1, $sort: { nome: 1 } }
            });
            setGruppi(response.data.data || []);
        } catch (error) {
            console.error("Errore caricamento gruppi:", error);
            notify("Errore nel caricamento gruppi", { type: 'error' });
        } finally {
            setLoadingGruppi(false);
        }
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'nome':
                if (!value.trim()) {
                    newErrors.nome = 'Il nome del piano è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nome = 'Il nome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.nome;
                }
                break;

            case 'prezzoMensile':
            case 'prezzoAnnuale':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors[name] = 'Il prezzo deve essere un numero positivo';
                } else {
                    delete newErrors[name];
                }
                break;

            case 'maxNumeroSquadre':
            case 'maxInfografiche':
            case 'maxPostGg':
            case 'maxPostWeek':
            case 'maxPostMonth':
            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors[name] = 'Deve essere un numero positivo';
                } else {
                    delete newErrors[name];
                }
                break;

            default:
                delete newErrors[name];
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
        validateField(name, value);
    };

    const validateForm = () => {
        const fieldsToValidate = ['nome', 'prezzoMensile', 'prezzoAnnuale', 'maxNumeroSquadre', 'maxInfografiche', 'maxPostGg', 'maxPostWeek', 'maxPostMonth', 'ordinamento'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            const dataToSend = {
                nome: formData.nome.trim(),
                gruppoId: formData.gruppoId ? parseInt(formData.gruppoId) : null,
                DescrizioneCompleta: formData.DescrizioneCompleta.trim() || null,
                maxNumeroSquadre: formData.maxNumeroSquadre ? parseInt(formData.maxNumeroSquadre) : 0,
                maxInfografiche: formData.maxInfografiche ? parseInt(formData.maxInfografiche) : 0,
                maxPostGg: formData.maxPostGg ? parseInt(formData.maxPostGg) : 0,
                maxPostWeek: formData.maxPostWeek ? parseInt(formData.maxPostWeek) : 0,
                maxPostMonth: formData.maxPostMonth ? parseInt(formData.maxPostMonth) : 0,
                imgQuality: formData.imgQuality.trim() || null,
                prezzoMensile: formData.prezzoMensile ? parseFloat(formData.prezzoMensile) : null,
                prezzoAnnuale: formData.prezzoAnnuale ? parseFloat(formData.prezzoAnnuale) : null,
                prezziPersonalizzati: formData.prezziPersonalizzati
                    .filter(p => p.mesi && p.prezzo)
                    .map(p => ({
                        mesi: parseInt(p.mesi),
                        prezzo: parseFloat(p.prezzo),
                        flAttivo: p.flAttivo !== undefined ? p.flAttivo : true
                    })),
                durataDisponibile: formData.durataDisponibile,
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile ? 1 : 0,
                flAttivo: formData.flAttivo ? 1 : 0,
                watermark: formData.watermark ? 1 : 0,
                flAnnullato: 0,
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : 0
            };

            const response = await axios.post(`${host}/tbl-piani`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            notify(`Piano "${formData.nome}" creato con successo`, { type: 'success' });
            navigate('/piani');

        } catch (error) {
            console.error("Errore creazione piano:", error);
            notify(error.response?.data?.message || "Errore nella creazione del piano", { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setFormData({
            nome: '', gruppoId: '', DescrizioneCompleta: '', maxNumeroSquadre: '',
            maxInfografiche: '', maxPostGg: '', maxPostWeek: '', maxPostMonth: '', imgQuality: '',
            prezzoMensile: '', prezzoAnnuale: '', prezziPersonalizzati: [],
            durataDisponibile: ['mensile', 'annuale'],
            note: '', flVisibile: true, flAttivo: true, watermark: false, ordinamento: ''
        });
        setErrors({});
        setTouched({});
    };

    React.useEffect(() => {
        setLoading(true);
        loadGruppi().finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>Caricamento dati...</Typography>
                </Box>
            </Box>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.nome.trim() && !hasErrors;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/piani')} sx={{ textDecoration: 'none' }}>
                        Piani
                    </Link>
                    <Typography variant="body2" color="text.primary">Nuovo Piano</Typography>
                </Breadcrumbs>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            📦 Crea Nuovo Piano
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare un nuovo piano
                        </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/piani')}>
                        Indietro
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Piano</Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Piano *"
                                            value={formData.nome}
                                            onChange={(e) => handleFieldChange('nome', e.target.value)}
                                            error={touched.nome && !!errors.nome}
                                            helperText={touched.nome && errors.nome}
                                            placeholder="Inserisci il nome del piano"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth error={touched.gruppoId && !!errors.gruppoId}>
                                            <InputLabel>Gruppo</InputLabel>
                                            <Select
                                                value={formData.gruppoId}
                                                label="Gruppo"
                                                onChange={(e) => handleFieldChange('gruppoId', e.target.value)}
                                                disabled={loadingGruppi}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <GroupsIcon sx={{ color: '#1565c0' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                <MenuItem value="">Nessuno</MenuItem>
                                                {gruppi.map(gruppo => (
                                                    <MenuItem key={gruppo.id} value={gruppo.id}>{gruppo.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Prezzo Mensile"
                                            type="number"
                                            value={formData.prezzoMensile}
                                            onChange={(e) => handleFieldChange('prezzoMensile', e.target.value)}
                                            error={touched.prezzoMensile && !!errors.prezzoMensile}
                                            helperText={touched.prezzoMensile && errors.prezzoMensile || "Prezzo abbonamento mensile"}
                                            placeholder="0.00"
                                            inputProps={{ min: 0, step: 0.01 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EuroIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Prezzo Annuale"
                                            type="number"
                                            value={formData.prezzoAnnuale}
                                            onChange={(e) => handleFieldChange('prezzoAnnuale', e.target.value)}
                                            error={touched.prezzoAnnuale && !!errors.prezzoAnnuale}
                                            helperText={touched.prezzoAnnuale && errors.prezzoAnnuale || "Prezzo abbonamento annuale"}
                                            placeholder="0.00"
                                            inputProps={{ min: 0, step: 0.01 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EuroIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Prezzi Personalizzati (es. 4 mesi, 6 mesi)
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddCircleOutlineIcon />}
                                                onClick={handleAddPrezzoPersonalizzato}
                                            >
                                                Aggiungi Prezzo
                                            </Button>
                                        </Box>

                                        {formData.prezziPersonalizzati.map((item, index) => (
                                            <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Numero Mesi"
                                                        type="number"
                                                        value={item.mesi}
                                                        onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'mesi', e.target.value)}
                                                        placeholder="es. 4"
                                                        inputProps={{ min: 1 }}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Prezzo"
                                                        type="number"
                                                        value={item.prezzo}
                                                        onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'prezzo', e.target.value)}
                                                        placeholder="0.00"
                                                        inputProps={{ min: 0, step: 0.01 }}
                                                        size="small"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <EuroIcon fontSize="small" />
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={item.flAttivo !== false}
                                                                onChange={(e) => handlePrezzoPersonalizzatoChange(index, 'flAttivo', e.target.checked)}
                                                                size="small"
                                                                color="success"
                                                            />
                                                        }
                                                        label={item.flAttivo !== false ? "Attivo" : "Inattivo"}
                                                    />
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRequestDeletePrezzo(index)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}

                                        {formData.prezziPersonalizzati.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                                Nessun prezzo personalizzato. Clicca "Aggiungi Prezzo" per aggiungerne uno.
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Descrizione Completa"
                                            multiline
                                            rows={3}
                                            value={formData.DescrizioneCompleta}
                                            onChange={(e) => handleFieldChange('DescrizioneCompleta', e.target.value)}
                                            placeholder="Descrizione dettagliata del piano..."
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Numero Squadre"
                                            type="number"
                                            value={formData.maxNumeroSquadre}
                                            onChange={(e) => handleFieldChange('maxNumeroSquadre', e.target.value)}
                                            error={touched.maxNumeroSquadre && !!errors.maxNumeroSquadre}
                                            helperText={touched.maxNumeroSquadre && errors.maxNumeroSquadre}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Infografiche"
                                            type="number"
                                            value={formData.maxInfografiche}
                                            onChange={(e) => handleFieldChange('maxInfografiche', e.target.value)}
                                            error={touched.maxInfografiche && !!errors.maxInfografiche}
                                            helperText={touched.maxInfografiche && errors.maxInfografiche}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Post al Giorno"
                                            type="number"
                                            value={formData.maxPostGg}
                                            onChange={(e) => handleFieldChange('maxPostGg', e.target.value)}
                                            error={touched.maxPostGg && !!errors.maxPostGg}
                                            helperText={touched.maxPostGg && errors.maxPostGg}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Post a Settimana"
                                            type="number"
                                            value={formData.maxPostWeek}
                                            onChange={(e) => handleFieldChange('maxPostWeek', e.target.value)}
                                            error={touched.maxPostWeek && !!errors.maxPostWeek}
                                            helperText={touched.maxPostWeek && errors.maxPostWeek}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Max Post al Mese"
                                            type="number"
                                            value={formData.maxPostMonth}
                                            onChange={(e) => handleFieldChange('maxPostMonth', e.target.value)}
                                            error={touched.maxPostMonth && !!errors.maxPostMonth}
                                            helperText={touched.maxPostMonth && errors.maxPostMonth}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Qualità Immagini"
                                            value={formData.imgQuality}
                                            onChange={(e) => handleFieldChange('imgQuality', e.target.value)}
                                            placeholder="es. HD, 4K, Standard"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare i piani"}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={2}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            placeholder="Note aggiuntive..."
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Anteprima Piano</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {formData.nome || 'Nome Piano'}
                                </Typography>
                                {(formData.prezzoMensile || formData.prezzoAnnuale) && (
                                    <Box sx={{ mb: 2 }}>
                                        {formData.prezzoMensile && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                                                <EuroIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                                <Typography variant="h6" color="primary">{formData.prezzoMensile}/mese</Typography>
                                            </Box>
                                        )}
                                        {formData.prezzoAnnuale && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <EuroIcon sx={{ color: '#2196f3', fontSize: 18 }} />
                                                <Typography variant="body1" color="text.secondary">{formData.prezzoAnnuale}/anno</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                {formData.DescrizioneCompleta && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {formData.DescrizioneCompleta.length > 80 ? `${formData.DescrizioneCompleta.substring(0, 80)}...` : formData.DescrizioneCompleta}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel
                                    control={<Switch checked={formData.flVisibile} onChange={(e) => handleFieldChange('flVisibile', e.target.checked)} color="primary" />}
                                    label="Piano visibile"
                                    sx={{ mb: 1 }}
                                />
                                <FormControlLabel
                                    control={<Switch checked={formData.flAttivo} onChange={(e) => handleFieldChange('flAttivo', e.target.checked)} color="primary" />}
                                    label="Piano attivo"
                                />
                                <FormControlLabel
                                    control={<Switch checked={formData.watermark} onChange={(e) => handleFieldChange('watermark', e.target.checked)} color="primary" />}
                                    label="Watermark"
                                />
                            </CardContent>
                        </Card>

                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Correggere i seguenti errori:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}><Typography variant="body2">{error}</Typography></li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Piano'}
                            </Button>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} disabled={saving}>
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">* Campi obbligatori</Typography>
                </Box>

            </motion.div>

            {/* Dialog Conferma Cancellazione */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Conferma cancellazione</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sei sicuro di voler cancellare questo prezzo personalizzato?
                        Questa azione è irreversibile. Se vuoi solo nasconderlo, puoi disattivarlo usando lo switch.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Annulla</Button>
                    <Button onClick={handleConfirmDeletePrezzo} color="error" autoFocus>
                        Cancella
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
